import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";

const MENTION_PREFIXES = [["account:", "account"], ["opportunity:", "opportunity"], ["prospect:", "prospect"]];

// Prompt textarea with @account: / @opportunity: / @prospect: autocomplete backed by /context/search.
export const useMentionPrompt = (userId) => {
  const [prompt, setPrompt] = useState("");
  const [mentions, setMentions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [results, setResults] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);

  const search = useCallback(async (query, type) => {
    if (!query) return setResults([]);
    try {
      const response = await axios.get(`${API}/context/search/${userId}`, { params: { query, type } });
      setResults(response.data.results || []);
    } catch (error) {
      console.error("Mention search error:", error);
      setResults([]);
    }
  }, [userId]);

  const handleChange = (e) => {
    const value = e.target.value;
    const position = e.target.selectionStart;
    setPrompt(value);
    setCursorPosition(position);
    const before = value.substring(0, position);
    const at = before.lastIndexOf("@");
    if (at === -1 || (at !== 0 && before[at - 1] !== " ")) return setShowDropdown(false);
    const text = before.substring(at + 1);
    const [prefix, type] = MENTION_PREFIXES.find(([p]) => text.startsWith(p)) || ["", "all"];
    search(text.substring(prefix.length), type);
    setShowDropdown(true);
  };

  const insertMention = (result) => {
    const before = prompt.substring(0, cursorPosition);
    const at = before.lastIndexOf("@");
    const mentionText = `@${result.type}:${result.name}`;
    setPrompt(prompt.substring(0, at) + mentionText + " " + prompt.substring(cursorPosition));
    setMentions((prev) => [...prev, result]);
    setShowDropdown(false);
    setResults([]);
    setTimeout(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      const pos = at + mentionText.length + 1;
      textareaRef.current.setSelectionRange(pos, pos);
    }, 0);
  };

  const removeMention = (idx) => setMentions((prev) => prev.filter((_, i) => i !== idx));

  const appendToken = (token) => {
    setPrompt((p) => p + token);
    textareaRef.current?.focus();
  };

  const reset = () => {
    setPrompt("");
    setMentions([]);
  };

  return { prompt, mentions, showDropdown, results, textareaRef, handleChange, insertMention, removeMention, appendToken, reset };
};
